/**
 * cart-addon.js
 * Add-on promotion cards — Liquid renders the HTML; this file handles
 * qty +/- interaction, ATC, and section swap via the Section Rendering API.
 *
 * Exposed as: window.CartAddon
 */

(function (global) {
  'use strict';

  /* ─── Localized strings (from [data-addon-promo] data-attrs; English defaults) ─ */
  var _STR = {
    adding:   'Adding…',
    added:    'Added',
    addToBag: 'Add to Bag',
    failed:   'Failed — try again',
  };
  function _loadStrings() {
    var w = document.querySelector('[data-addon-promo]');
    if (!w) return;
    if (w.dataset.strAdding)   _STR.adding   = w.dataset.strAdding;
    if (w.dataset.strAdded)    _STR.added    = w.dataset.strAdded;
    if (w.dataset.strAddToBag) _STR.addToBag = w.dataset.strAddToBag;
    if (w.dataset.strFailed)   _STR.failed   = w.dataset.strFailed;
  }

  /* ─── ATC button states ──────────────────────────────────────────────────── */
  function _setLoading(btns) {
    btns.forEach(function (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-label', _STR.adding);
    });
  }

  function _setAdded(btns) {
    btns.forEach(function (btn) {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.setAttribute('aria-label', _STR.added);
    });
    setTimeout(function () {
      btns.forEach(function (btn) {
        btn.classList.remove('is-added');
        btn.setAttribute('aria-label', _STR.addToBag);
      });
    }, 2000);
  }

  function _setError(btns) {
    btns.forEach(function (btn) {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.classList.add('is-error');
      btn.setAttribute('aria-label', _STR.failed);
    });
    setTimeout(function () {
      btns.forEach(function (btn) {
        btn.classList.remove('is-error');
        btn.setAttribute('aria-label', _STR.addToBag);
      });
    }, 2000);
  }

  /* ─── Section swap via Section Rendering API ────────────────────────────── */
  function _swapSection(html) {
    if (!html) return false;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;

    // Swap addon cards when section rendering includes them.
    // NOTE: the section rendering API may omit #cart-layout__cart-addon when
    // section.blocks is unavailable in nested snippet context — that's fine;
    // the cart item list below is always present and is the critical update.
    var fresh = tmp.querySelector('#cart-layout__cart-addon');
    var wrapper = document.getElementById('cart-layout__cart-addon');
    var swapped = false;
    if (fresh && wrapper) {
      wrapper.innerHTML = fresh.innerHTML;
      swapped = true;
    }

    // cart-items.js skips re-render when data-server-rendered is set;
    // swap the wrapper (covers both non-empty and empty-cart states where
    // [data-cart-items] may not exist yet).
    var freshWrapper = tmp.querySelector('[data-cart-items-wrapper]');
    var currentWrapper = document.querySelector('[data-cart-items-wrapper]');
    if (freshWrapper && currentWrapper) {
      currentWrapper.innerHTML = freshWrapper.innerHTML;
    }

    _init();
    return swapped;
  }

  /* ─── Sync dependent modules after the cart mutates ──────────────────────── */
  // CartItems re-syncs totals / free-shipping bar; the header badge refreshes
  // from an authoritative /cart.js read.
  function _afterCartMutated() {
    if (global.CartItems) global.CartItems.reload();

    if (global.$360 && global.$360.updateHeaderCartCount) {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) { global.$360.updateHeaderCartCount(cart); });
    }
  }

  /* ─── Re-render the cart page section (external adds — quick-add / ATC) ───── */
  // Used when a product is added to the cart from outside this module (the
  // quick-add popup, product cards in "You May Also Like", etc.). Those paths
  // don't request the cart-layout section, so we fetch it here and swap in the
  // fresh item list + addon cards. Line-item rows are server-rendered, so this
  // is the only way a newly added product appears without a full page reload.
  function _refresh(cb) {
    fetch('/cart?sections=cart-layout', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('section fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        _swapSection(data && data['cart-layout']);
        _afterCartMutated();
        if (cb) cb();
      })
      .catch(function (err) { console.error('[CartAddon] refresh', err); });
  }

  /* ─── Add to cart ────────────────────────────────────────────────────────── */
  function _addToCart(item) {
    var variantId = parseInt(item.getAttribute('data-addon-variant-id'), 10);
    var qty = parseInt(item.getAttribute('data-addon-qty'), 10) || 1;
    var btns = Array.prototype.slice.call(item.querySelectorAll('.cart-addon-item__atc'));
    if (!variantId) return;
    _setLoading(btns);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: qty,
        sections: 'cart-layout',
        sections_url: '/cart',
      }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('ATC failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var swapped = _swapSection(data && data.sections && data.sections['cart-layout']);
        // After swap, find refreshed buttons for the same variant; fall back to originals
        var addedBtns = btns;
        if (swapped) {
          var newItem = document.querySelector('[data-addon-variant-id="' + variantId + '"]');
          if (newItem) addedBtns = Array.prototype.slice.call(newItem.querySelectorAll('.cart-addon-item__atc'));
        }
        _setAdded(addedBtns);

        _afterCartMutated();
      })
      .catch(function (err) {
        console.error('[CartAddon]', err);
        _setError(btns);
      });
  }

  /* ─── Per-item event binding ─────────────────────────────────────────────── */
  function _initItem(item) {
    if (item.dataset.addonInited) return;
    item.dataset.addonInited = '1';
    var minusBtns = item.querySelectorAll('.cart-addon-qty-minus');
    var plusBtns  = item.querySelectorAll('.cart-addon-qty-plus');
    var available = item.getAttribute('data-addon-available') !== 'false';

    function updateQty(delta) {
      var current = parseInt(item.getAttribute('data-addon-qty'), 10) || 1;
      var next = Math.max(1, current + delta);
      item.setAttribute('data-addon-qty', next);
      item.querySelectorAll('.cart-addon-qty-display').forEach(function (el) {
        el.textContent = next;
      });
      minusBtns.forEach(function (btn) { btn.disabled = next <= 1; });
    }

    minusBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { updateQty(-1); });
    });
    plusBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { updateQty(1); });
    });

    if (available) {
      item.querySelectorAll('.cart-addon-item__atc').forEach(function (btn) {
        btn.addEventListener('click', function () { _addToCart(item); });
      });
    }
  }

  /* ─── Boot / re-init ─────────────────────────────────────────────────────── */
  function _init() {
    _loadStrings();
    document.querySelectorAll('.cart-addon-item').forEach(_initItem);
  }

  document.addEventListener('DOMContentLoaded', _init);

  /* ─── Exports ────────────────────────────────────────────────────────────── */
  global.CartAddon = { init: _init, refresh: _refresh };

})(window);
